'use client';

import { useFormStatus } from 'react-dom';
import { useEffect, useState, useActionState } from 'react';
import { SaveBuyNowForm } from '@/app/lib/actions';

import { Link } from '@/i18n/routing';
import { FaSpinner } from 'react-icons/fa';
import { FaLandmark } from 'react-icons/fa6';
import { FaRegCreditCard } from 'react-icons/fa6';
import PaymentGateways from './pay-gateways';
import { useRouter } from '@/i18n/routing';
import PaypalButtons from './paypal-buttons';
import { CreateRazorPayOrder } from '@/app/lib/initiate-payments';
import { VerifyRazorpayPayment } from '@/app/lib/initiate-payments';
import { getEncryptedOrder } from '@/app/lib/CCAvenue';
import ShortUniqueId from 'short-unique-id';
import { useTranslations } from 'next-intl';

const { randomUUID } = new ShortUniqueId({ length: 10 });

function BuyButton({ state, regionalBtn }) {
  const { pending } = useFormStatus();

  return (
    <button
      disabled={pending || state}
      type="submit"
      className="flex w-1/2 mx-auto justify-center rounded-md hover:cursor-pointer bg-gradient-to-r from-orange-500 to-pink-500 px-3 p-2 text-sm font-semibold leading-6 text-white disabled:opacity-75"
    >
      {pending ? (
        <div className="flex" disabled={pending}>
          <FaSpinner className="animate-spin me-2" size={'25'} />{' '}
          <span>Processing..</span>
        </div>
      ) : (
        regionalBtn
      )}
    </button>
  );
}

export default function BuyNowForm({ orderAmount, reportId, countryList }) {
  const [formState, action] = useActionState(SaveBuyNowForm, {
    success: false,
  });
  const [payOption, setPayOption] = useState('paymentGateways');
  const [gateWay, setGateway] = useState('razorPay');
  const [isPaypal, setIsPayPal] = useState(false);
  const [pending, setPending] = useState(false);
  const router = useRouter();
  const regionalText = useTranslations('buyNow');

  orderAmount = parseInt(orderAmount);

  //Crating the CCAvenue Payment
  async function CallCCAvenue(userDetails) {
    const host = 'https://www.coherentmi.com';

    try {
      const paymentData = {
        merchant_id: process.env.NEXT_PUBLIC_MERCHANT_ID, // Merchant ID (Required)
        order_id: randomUUID(), // Order ID - It can be generated from our project
        amount: orderAmount, // Payment Amount (Required)
        currency: 'USD', // Payment Currency Type (Required)
        billing_email: userDetails.email, // Billing Email (Optional)
        billing_name: userDetails.name, // Billing Name (Optional)
        billing_address: userDetails.address || '', // Billing Address (Optional)
        billing_zip: userDetails.zipCode || '', // Billing Zip (Optional)
        billing_country: userDetails.country || '', // Billing COuntry (Optional)
        redirect_url: `${host}/api/handle-CCAvenue`, // Success URL (Required)
        cancel_url: `${host}/api/handle-CCAvenue`, // Failed/Cancel Payment URL (Required)
        language: 'EN', // Language (Optional)
        billing_tel: userDetails.phone || '', // Billing Mobile Number (Optional)
      };

      let encReq = await getEncryptedOrder(paymentData);

      let accessCode = process.env.NEXT_PUBLIC_ACCESS_CODE;
      let URL = `https://secure.ccavenue.com/transaction/transaction.do?command=initiateTransaction&merchant_id=${paymentData.merchant_id}6&encRequest=${encReq}&access_code=${accessCode}`;
      router.push(URL);
    } catch (err) {
      console.log(err);
    }
  }

  //Creating razorpay payment
  async function CallRazorpay(userDetails) {
    const KEY_ID = process.env.NEXT_PUBLIC_RAZORPAY_CLIENT_ID;

    //Handle The payment Success
    async function handleSuccess(response) {
      const transaction = {
        id: response.razorpay_payment_id,
        razorpay_order_id: response.razorpay_order_id,
      };

      const status = await VerifyRazorpayPayment(transaction.id);

      if (status === 'captured') {
        router.push(
          `/payment-success?transactionId=${transaction.id}&transactionStatus=${status}`
        );
      }

      if (status === 'failed') {
        router.push(
          `/payment-success?transactionId=${transaction.id}&transactionStatus=${status}`
        );
      }
    }

    try {
      const order = await CreateRazorPayOrder(orderAmount, 'USD');
      const keyword = userDetails.keyword;
      const options = {
        key: KEY_ID, // Enter the Key ID generated from the Dashboard
        amount: order.amount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
        currency: order.currency,
        name: 'CoherentMI',
        description: keyword,
        image: 'https://www.coherentmi.com/logo.webp',
        order_id: order.order_id,
        handler: handleSuccess,
        prefill: {
          name: userDetails.name,
          email: userDetails.email,
          contact: userDetails.phone || '',
        },
        theme: {
          color: '#3399cc',
        },
      };

      var payButton = new window.Razorpay(options);
      payButton.on('payment.failed', function (response) {
        alert(response.error.description);
        alert(response.error.reason);
      });
      payButton.open();
    } catch (err) {
      console.log(err);
    }
  }

  //To handle change in pyament option
  function handlePayOptionChange(e) {
    setPayOption(e.target.value);
    if (isPaypal && payOption === 'bankWire') {
      setGateway('ccAvenue');
    }
  }

  //To store the used gateway to session
  useEffect(() => {
    sessionStorage.setItem('gateway', gateWay);
  }, [gateWay]);

  //This Effect Will Run After the Form Submission is Done.
  useEffect(() => {
    async function handlePayment() {
      const details = formState.data;
      if (gateWay === 'ccAvenue') {
        await CallCCAvenue(details);
      } else if (gateWay === 'razorPay') {
        await CallRazorpay(details);
      } else if (gateWay === 'payPal') {
        setIsPayPal(true);
      }
    }

    if (formState.success) {
      if (payOption === 'paymentGateways') {
        handlePayment();
      } else if (payOption === 'bankWire') {
        router.push('/bank-wire-payments/thank-you');
      }
    }
  }, [formState]); //eslint-disable-line

  return (
    <>
      <div className="w-full">
        <form action={action} disabled={formState.success}>
          <div className="bg-[#1c327beb] text-white text-center py-2 rounded-t-md">
            <h4 className="mb-0 font-bold text-xl">
              {regionalText('form.title')}
            </h4>
          </div>
          <div className="space-y-3 shadow-xl p-2 rounded-b-md bg-white">
            <input value={'Buy Now'} type="hidden" name="formType" />
            <input value={reportId} type="hidden" name="reportId" />
            <input value={orderAmount} type="hidden" name="orderAmount" />
            <div className="relative">
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  disabled={formState.success}
                  placeholder=" "
                  className="peer block w-full rounded-t-md p-2 text-gray-900 shadow-sm border border-transparent border-b-gray-300 border-b-1 focus:ring-indigo-200 focus:ring-opacity-50 placeholder:text-gray-400"
                />
                <label
                  htmlFor="name"
                  className="absolute left-2 top-4 text-xs text-gray-700 transition-all peer-focus:-top-0.5 peer-focus:text-blue-500 peer-focus:text-xs not:peer-placeholder-shown:top-1 peer-placeholder-shown:text-sm"
                >
                  {regionalText('form.name')}{' '}
                  <span className="text-red-400">*</span>
                </label>
              </div>
              {formState.errors && (
                <p className=" text-red-600 text-start">
                  {formState.errors.name}
                </p>
              )}
            </div>
            <div>
              <div className="mt-2 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  disabled={formState.success}
                  placeholder=" "
                  className="peer block w-full rounded-t-md p-2 text-gray-900 shadow-sm border border-transparent border-b-gray-300 border-b-1 focus:ring-indigo-200 focus:ring-opacity-50 placeholder:text-gray-400"
                />
                <label
                  htmlFor="email"
                  className="absolute left-2 top-3.5 text-xs text-gray-700 transition-all peer-focus:-top-0.5 peer-focus:text-blue-500 peer-focus:text-sm not:peer-placeholder-shown:top-0.5 peer-placeholder-shown:text-sm"
                >
                  {regionalText('form.email')}{' '}
                  <span className="text-red-400">*</span>
                </label>
              </div>
              {formState.errors && (
                <p className=" text-red-600 text-start">
                  {formState.errors.email}
                </p>
              )}
            </div>
            <div>
              <div className="mt-2 flex gap-2">
                <select
                  id="country"
                  name="country"
                  required
                  disabled={formState.success}
                  className="form-select text-sm text-gray-900 shrink w-1/4 rounded-t-md appearance-none border border-transparent border-b-gray-300 border-b-1 p-2 block shadow-sm focus:ring-indigo-200 focus:ring-opacity-50 "
                >
                  <option>{regionalText('form.country')}</option>
                  {countryList.map((item) => {
                    return (
                      <option key={item.name} value={item.name}>
                        {item.name}(+ {item.dailcode})
                      </option>
                    );
                  })}
                </select>
                <div className="relative w-full">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    disabled={formState.success}
                    placeholder=" "
                    className="peer block w-full rounded-t-md p-2 text-gray-900 shadow-sm border border-transparent border-b-gray-300 border-b-1 focus:ring-indigo-200 focus:ring-opacity-50 placeholder:text-gray-400"
                  />
                  <label
                    htmlFor="phone"
                    className="absolute left-2 top-3.5 text-xs text-gray-700 transition-all peer-focus:-top-0.5 peer-focus:text-blue-500 peer-focus:text-sm not:peer-placeholder-shown:top-0.5 peer-placeholder-shown:text-sm"
                  >
                    {regionalText('form.phone')}{' '}
                    <span className="text-red-400">*</span>
                  </label>
                </div>
              </div>
              {formState.errors && (
                <p className=" text-red-600 text-start text-sm">
                  {formState.errors.phone}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <div className="mt-2 relative flex-1">
                <input
                  rows={3}
                  id="address"
                  name="address"
                  type="text"
                  placeholder=""
                  required
                  disabled={formState.success}
                  className="peer block w-full rounded-t-md p-2 text-gray-900 shadow-sm border border-transparent border-b-gray-300 border-b-1 focus:ring-indigo-200 focus:ring-opacity-50 placeholder:text-gray-400"
                />
                <label
                  htmlFor="address"
                  className="absolute left-2 top-3.5 text-xs text-gray-700 transition-all peer-focus:-top-0.5 peer-focus:text-blue-500 peer-focus:text-sm not:peer-placeholder-shown:top-0.5 peer-placeholder-shown:text-sm"
                >
                  {regionalText('form.add')}{' '}
                  <span className="text-red-400">*</span>
                </label>
              </div>
              {formState.errors && (
                <p className=" text-red-600 text-start">
                  {formState.errors.address}
                </p>
              )}
              <div>
                <div className="mt-2 relative">
                  <input
                    rows={3}
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    placeholder=""
                    required
                    disabled={formState.success}
                    className="peer block w-full rounded-t-md p-2 text-gray-900 shadow-sm border border-transparent border-b-gray-300 border-b-1 focus:ring-indigo-200 focus:ring-opacity-50 placeholder:text-gray-400"
                  />
                  <label
                    htmlFor="zipCode"
                    className="absolute left-2 top-3.5 text-xs text-gray-700 transition-all peer-focus:-top-0.5 peer-focus:text-blue-500 peer-focus:text-sm not:peer-placeholder-shown:top-0.5 peer-placeholder-shown:text-sm"
                  >
                    {regionalText('form.zip')}{' '}
                    <span className="text-red-400">*</span>
                  </label>
                </div>
                {formState.errors && (
                  <p className=" text-red-600 text-start">
                    {formState.errors.zipcode}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-1 w-full mx-auto shadow-md p-2 rounded-md bg-white">
            {isPaypal &&
            formState.success &&
            payOption === 'paymentGateways' ? (
              <div className="mt-2">
                <p className="font-semibold text-sm text-center mb-3">
                  Please procide with the Secure{' '}
                  <span className="text-blue-500 italic">PayPal</span> Gateway
                </p>
                <PaypalButtons
                  orderAmount={orderAmount}
                  pending={pending}
                  setPending={setPending}
                />
              </div>
            ) : (
              <div>
                <div className="justify-start flex flex-col gap-y-4 items-center mt-2">
                  <h2 className="font-bold text-start w-full">
                    {regionalText('payment_mode')}
                  </h2>
                  <div className="flex w-full gap-3">
                    <div className="p-2 border border-solid border-gray-300 flex items-center rounded-md">
                      <input
                        id="debit/credit"
                        type="checkbox"
                        name="paymentOption"
                        value={'paymentGateways'}
                        onChange={handlePayOptionChange}
                        className="p-1 me-3 rounded-md text-blue-600 focus:ring-0 cursor-pointer"
                        checked={payOption === 'paymentGateways'}
                      />
                      <label htmlFor="debit/credit" className="cursor-pointer">
                        <FaRegCreditCard size={35} fill="black" />
                        <span className="text-black">
                          {regionalText('gateway')}
                        </span>
                      </label>
                    </div>
                    <div className="p-3 border border-solid border-gray-300 flex items-center  rounded-md">
                      <input
                        id="bankwire"
                        type="checkbox"
                        name="paymentOption"
                        value={'bankWire'}
                        onChange={handlePayOptionChange}
                        className="p-1 me-3 rounded-md text-blue-600 focus:ring-0 cursor-pointer"
                        checked={payOption === 'bankWire'}
                      />
                      <label htmlFor="bankwire" className="cursor-pointer">
                        <FaLandmark size={35} fill="black" />{' '}
                        <span className="text-black">
                          {regionalText('bank_wire')}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="w-full justify-start">
                    {payOption === 'paymentGateways' ? (
                      <PaymentGateways
                        orderAmount={orderAmount}
                        formState={formState}
                        gateWay={gateWay}
                        setGateway={setGateway}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                </div>
                <div className="my-2">
                  <input
                    id="privacyPolicy"
                    name="privacyPolicy"
                    type="checkbox"
                    defaultChecked
                    required
                    disabled={formState.success}
                    className="rounded-sm border-0 shadow-sm"
                  />
                  <label htmlFor="privacyPolicy" className="ms-2 text-black">
                    <span className="text-xs text-black">
                      {regionalText('privacy')}
                    </span>
                    <Link
                      href="/privacy-policy"
                      className="text-blue-400 hover:text-blue-700"
                    >
                      <span className="text-xs ms-1">
                        {regionalText('link')}.
                      </span>
                    </Link>
                  </label>
                </div>
                <BuyButton
                  state={formState.success}
                  regionalBtn={regionalText('btn')}
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
